import { Box, Button } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ContentSettings, IKpiAlertEventFilters, IKpiAlertRule, RendererFilter } from '@report-content';
import { instance } from 'api/api';
import PanelWrapper from './PanelWrapper';
import ReactSelect from 'components/common/react-select/ReactSelect';
import { FormComponentValue } from 'components/common/form/layout/control';
import useBundleTranslation from 'i18n';
import IconMi from 'components/common/icon/IconMi';
import GridComponent from 'components/common/grid/GridComponent';
import { Popup } from 'components/common/popup/Popup';
import ReportAlertRuleWizard from 'app/report/report-alert-rule-wizard/ReportAlertRuleWizard';

interface IAlertEvent {
    filters: IKpiAlertEventFilters;
    title: string;
}
export default function AlertRulesMenu({
    contentSettings,
    onApplyFilters,
}: {
    contentSettings: ContentSettings;
    onApplyFilters: (filters: IKpiAlertEventFilters) => void;
}) {
    const { t } = useBundleTranslation(['components/report-content']);
    const {
        data,
        isLoading,
        refetch: refetchTriggeredAlerts,
    } = useQuery<Array<IAlertEvent>, Error>({
        queryKey: ['triggered-alerts', contentSettings.elementId, contentSettings.measurement_time],
        queryFn: async () => {
            const response = await instance.get(`/data/report/triggered-alerts/${contentSettings.elementId}`, {
                params: {
                    measurement_time: contentSettings.measurement_time,
                },
            });
            return response.data.status == 'OK' ? response.data.data : [];
        },
        refetchOnMount: true,
    });

    const {
        data: alertsList,
        refetch,
        isLoading: listIsLoading,
    } = useQuery<Array<IKpiAlertRule>, Error>({
        queryKey: ['alerts_for_kpi_to_add', contentSettings.elementId],
        queryFn: async () => {
            if (contentSettings.context == 'view') {
                return [];
            }
            const response = await instance.get(
                `/data/editor/report/${contentSettings.elementId}/alert-rule-for-kpi-for-add`,
            );
            return response.data.status == 'OK' ? response.data.data : [];
        },
        refetchOnMount: true,
    });

    const [isPopupOpen, setIsPopupOpen] = useState(false);

    const applyFilters = (filtersIndex: number) => {
        let filters = {};
        if (data?.[filtersIndex]) {
            filters = data[filtersIndex].filters;
        }
        onApplyFilters(filters);
    };

    const [selectedAlertIndex, setSelectedAlertIndex] = React.useState<string>('-1');
    const allowedAlertOptions: FormComponentValue[] = useMemo(() => {
        const emptyValue = [
            {
                label: t('kpi_chart.alert_triggered', { count: data?.length ?? 0 }),
                value: '-1',
            },
        ];
        return emptyValue.concat(
            data
                ? data.map((alert, index) => ({
                      label: alert.title,
                      value: String(index),
                  }))
                : [],
        );
    }, [data]);

    // Always show the menu in edit mode, even if there are no Alerts, to allow users to add new Alert Rules.
    if (isLoading || listIsLoading || (allowedAlertOptions.length <= 1 && contentSettings.context == 'view')) {
        return null;
    }

    const hasAlerts = (alertsList?.length ?? 0) > 0;

    return (
        <Box sx={{ mb: 2 }}>
            <PanelWrapper>
                <Box sx={{ display: 'inline-flex', m: 0.75 }}>
                    <ReactSelect
                        dataTest={'triggered-alerts-select'}
                        data={allowedAlertOptions}
                        update={(value) => {
                            setSelectedAlertIndex(value);
                            applyFilters(Number(value));
                        }}
                        value={selectedAlertIndex}
                    />
                </Box>
                {contentSettings.context == 'edit' && (
                    <Box sx={{ m: 0.75 }}>
                        <Button
                            sx={{ whiteSpace: 'nowrap' }}
                            startIcon={<IconMi icon="gear" />}
                            onClick={() => {
                                setIsPopupOpen(true);
                            }}
                            data-test={'edit-alerts-button'}
                        >
                            {t(`kpi_chart.${hasAlerts ? 'edit_alert_rules' : 'add_alert_rule'}`)}
                        </Button>

                        {hasAlerts && (
                            <Popup
                                settings={{
                                    noCancel: true,
                                    title: t('alert_rules'),
                                    textOK: t('ok'),
                                }}
                                open={isPopupOpen}
                                onHide={() => {
                                    setIsPopupOpen(false);
                                    refetch();
                                    refetchTriggeredAlerts();
                                }}
                            >
                                <GridComponent
                                    uid={`reportAlertRuleGrid`}
                                    settingsUrl={`/data/editor/element/report/${contentSettings.elementId}/alert-rule/grid`}
                                    formGrid
                                />
                            </Popup>
                        )}
                        {!hasAlerts && isPopupOpen && (
                            <ReportAlertRuleWizard
                                elementId={contentSettings.elementId}
                                alertRuleId={0}
                                isOpen={isPopupOpen}
                                setIsOpen={(open) => {
                                    if (!open) {
                                        setIsPopupOpen(false);
                                        refetch();
                                        refetchTriggeredAlerts();
                                    }
                                }}
                            />
                        )}
                    </Box>
                )}
            </PanelWrapper>
        </Box>
    );
}
